/** Shared config for google-map-react — every map must use the same libraries on first load. */
export const GOOGLE_MAPS_BOOTSTRAP_URL_KEYS = {
  key: process.env.REACT_APP_GOOGLE_API_KEY,
  libraries: ["places", "geometry"],
};

let loadPromise = null;

export const isGoogleMapsPlacesReady = () =>
  Boolean(window.google?.maps?.places);

const getExistingMapsScript = () =>
  document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');

const removeIncompleteMapsScript = () => {
  const existing = getExistingMapsScript();
  if (!existing || isGoogleMapsPlacesReady()) return;

  existing.remove();
  if (window.google && !window.google.maps?.places) {
    try {
      delete window.google;
    } catch {
      window.google = undefined;
    }
  }
  loadPromise = null;
};

/** Load Maps + Places once for the session (used by google-map-react and Places autocomplete). */
export const loadGoogleMapsApi = () => {
  if (isGoogleMapsPlacesReady()) {
    return Promise.resolve(window.google.maps);
  }

  if (loadPromise) return loadPromise;

  removeIncompleteMapsScript();

  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
  if (!apiKey) {
    return Promise.reject(new Error("REACT_APP_GOOGLE_API_KEY is not set"));
  }

  loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    const params = new URLSearchParams({
      key: apiKey,
      libraries: GOOGLE_MAPS_BOOTSTRAP_URL_KEYS.libraries.join(","),
    });
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (isGoogleMapsPlacesReady()) {
        resolve(window.google.maps);
      } else {
        loadPromise = null;
        reject(new Error("Google Maps Places library failed to load"));
      }
    };
    script.onerror = () => {
      loadPromise = null;
      reject(new Error("Google Maps script failed to load"));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
};
